import { apiGet, apiPost } from "./apiClient";

// Admin API for the Intuit Enterprise Suite (IES) integration. The billing
// provider switch itself is environment-driven (BILLING_PROVIDER), so the UI
// reports it rather than toggling it.

export type IESRealmStatus = {
  realmId: string;
  hasTokens: boolean;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  tokensUpdatedAt?: string;
  lastSyncAt?: string;
};

export type IESStatus = {
  iesEnabled: boolean;
  // The redirect URI registered on the Intuit app (callback host).
  redirectUri?: string;
  configured: boolean;
  reason?: string;
  environment: string;
  realms: IESRealmStatus[];
};

export type IESEnabledInfo = {
  enabled: boolean;
  configured: boolean;
  reason?: string;
};

// Intuit ingestion state. BILL is always available; this only controls the
// Intuit side (webhooks, sync, Intuit-origin payments).
export async function getIESEnabled(): Promise<IESEnabledInfo> {
  return apiGet({ endpoint: "/admin/ies-enabled", withAuth: true });
}

// Turns Intuit ingestion on or off at runtime (stored in settings, applied
// without a restart).
export async function setIESEnabled(enabled: boolean) {
  return apiPost({
    endpoint: "/admin/ies-enabled",
    data: { enabled },
    withAuth: true,
  });
}

export type IESSyncResult = {
  realmId: string;
  ok: boolean;
  error?: string;
  durationMs: number;
};

export type IESConnectInfo = {
  authorizeUrl: string;
  redirectUri: string;
  scopes: string;
  environment: string;
  expiresIn: number;
};

// Current provider, configuration and per-realm connection state.
export async function getIESStatus(): Promise<IESStatus> {
  return apiGet({ endpoint: "/admin/ies-status", withAuth: true });
}

// Runs the sync for every configured realm and returns a per-realm result.
export async function runIESSync(): Promise<{ message: string; results: IESSyncResult[] }> {
  return apiPost({ endpoint: "/admin/ies-sync", data: {}, withAuth: true });
}

// One-time Intuit consent URL for connecting a new realm.
export async function getIESConnectUrl(): Promise<IESConnectInfo> {
  return apiGet({ endpoint: "/admin/ies-connect-url", withAuth: true });
}

// Completes the consent flow when the redirect lands on the portal: the
// parameters are forwarded to the API, which validates the one-time state and
// exchanges the code for tokens.
export async function completeIESOAuth(
  code: string,
  state: string,
  realmId: string
) {
  return apiPost({
    endpoint: "/ies/oauth/complete",
    data: { code, state, realmId },
    withAuth: false,
  });
}
